// =====================================================================
// Chamados › Indicadores — O PAINEL DE SLA (Julia, 2026-09-09).
//
// UM indicador com meta: **% de chamados atendidos dentro do SLA**, meta 90%.
// Foi o pedido literal ("acompanharia inicialmente um único indicador"), e o
// resto da tela existe para tornar esse número LEGÍVEL — não para competir com ele.
//
// ⚠️⚠️ A RÉGUA NÃO ESTÁ AQUI. Prazos, dias úteis, pausa e o critério de
//    dentro/fora moram no `server/chamados/chamados-sla.js`, puro e testado. Esta tela recebe o veredito pronto de `GET /indicadores` e desenha.
//    Reimplementar qualquer pedaço criaria duas verdades sobre o mesmo número — e
//    é o tipo de divergência que ninguém percebe, porque as duas dão um
//    percentual plausível.
//
// ⚠️⚠️ OS TRÊS NÚMEROS QUE ACOMPANHAM O % NÃO SÃO ENFEITE, cada um responde a um
//    achado medido em 09/09:
//      • SEM PRIORIDADE  → 11 dos 28 chamados (40%) não têm prazo aplicável. Se
//        este número não estiver à vista, o % se lê como se descrevesse a fila
//        inteira, e ele descreve menos da metade dela.
//      • DISTRIBUIÇÃO    → os 12 triados eram TODOS "Média". Com uma faixa só em
//        uso, a tabela de três prazos é decorativa, e sem este bloco ninguém nota.
//      • COM PAUSA       → finalizar pausa o relógio, logo quem prorroga move o
//        próprio prazo. A contramedida escolhida NÃO foi travar (travar faz o
//        técnico não registrar e o chamado apodrecer) — foi MEDIR.
// =====================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import ChamadosPainelGestao from './ChamadosPainelGestao';
import useTelaMobile from '../../../utils/useTelaMobile';
import { AlertTriangle, CheckCircle2, Clock, PauseCircle, HelpCircle } from 'lucide-react';

const API = '/api/chamados';
const COR = 'var(--ch-cor)';
const COR_CLARA = 'var(--ch-cor-clara)';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const MS_HORA = 3600000;

// As cores das três situações. ⚠️ MAPA e não ternário: são TRÊS estados
// ('dentro'/'fora'/'aguardando') e o `? :` de dois ramos pintaria "aguardando"
// com a cor de "fora" — mostrando como atraso o chamado que ainda está no prazo.
const COR_SITUACAO = {
  dentro: '#059669',
  fora: '#dc2626',
  aguardando: '#64748b',
};

const ROTULO_SITUACAO = {
  dentro: 'No prazo',
  fora: 'Fora do prazo',
  aguardando: 'Ainda no prazo',
};

// A ordem das faixas empilhadas, de baixo para cima.
//
// ⚠️ Existe para o gráfico ser DERIVADO dos dois mapas acima, em vez de repetir
//    'No prazo'/'Fora do prazo' como literais nos `dataKey` e nas cores. Com os
//    literais, renomear uma situação exigia acertar quatro lugares — e errar um
//    deles dá uma barra que simplesmente não desenha, sem erro no console.
const SITUACOES = ['dentro', 'fora', 'aguardando'];

// O sinalzinho de cada faixa. ⚠️ Fica na TELA e não vem do servidor: emoji é
// apresentação pura, e mandá-lo pela API misturaria régua com enfeite.
// ⚠️ Mapa com fallback (`|| ''`): faixa nova sem sinal sai sem o emoji, e não
// quebra a linha da tabela.
const SINAL_PRIORIDADE = { Alta: '🔴', Media: '🟡', Baixa: '🟢' };

/**
 * Duração em ms → texto curto ('4h', '2d 3h').
 *
 * ⚠️ Fala em DIAS de 24h porque é isso que a régua mede: um "dia útil" do SLA
 *    vale as 24 horas dele (ver `chamados-sla.js`). Traduzir para "8h de
 *    expediente" aqui faria a tela discordar do cálculo que produziu o número.
 */
const fmtDuracao = (ms) => {
  if (ms == null) return '—';
  const horas = Math.floor(ms / MS_HORA);
  if (horas < 24) return `${horas}h`;
  const dias = Math.floor(horas / 24);
  const resto = horas % 24;
  return resto ? `${dias}d ${resto}h` : `${dias}d`;
};

const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

const MESES_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/**
 * 'AAAA-MM' → 'setembro de 2026'.
 *
 * ⚠️ CORTA A STRING em vez de usar `new Date('2026-09')`: aquele é interpretado
 *    como UTC e, num fuso negativo, `toLocaleDateString` devolve AGOSTO. O mês
 *    do rótulo passaria a discordar do mês que o filtro pediu ao servidor.
 * ⚠️ Mês fora de 1..12 (dado torto na URL) devolve a string crua, em vez de
 *    `undefined de 2026`.
 */
const rotuloDoMes = (m) => {
  const [ano, mm] = String(m || '').split('-');
  const nome = MESES_PT[Number(mm) - 1];
  return nome ? `${nome} de ${ano}` : String(m);
};

// ⚠️ `equipe` vem do HUB (a faixa de fila). Este painel não escolhe a fila: o
//    SLA de T.I e o da Manutenção são painéis diferentes, e quem diz qual é a
//    faixa lá em cima — um `<select>` aqui seria um segundo lugar de trocar.
const ChamadosIndicadores = ({ equipe = 'TI' }) => {
  // ⚠️ O painel usa isto para encolher o rótulo do eixo do ranking: no celular,
  //    230px de nome de equipamento não deixam largura para a barra.
  const { ehMobile } = useTelaMobile();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // OS DOIS FILTROS — mês (AAAA-MM) e loja.
  //
  // ⚠️⚠️ ELES VÃO PARA O SERVIDOR, e não podem ser da tela: o produto desta
  //    página é um AGREGADO. Filtrar aqui obrigaria a recalcular o
  //    `apurarIndicador` no cliente — uma segunda implementação da régua do SLA,
  //    e as duas dariam percentuais plausíveis e diferentes. É o oposto da
  //    Consulta, onde os filtros são do cliente porque lá o produto é a LISTA.
  //
  // ⚠️ `''` significa TODOS nos dois, e o padrão é o mês vazio de propósito: uma
  //    fatia mensal do piloto tem 5 chamados, e um percentual sobre 2 casos não
  //    descreve nada. Abrir no retrato inteiro e deixar a pessoa estreitar é mais
  //    honesto que abrir num número instável. ⚠️ E evita a segunda busca que
  //    "abrir no mês mais recente" exigiria (a tela só sabe quais meses existem
  //    DEPOIS da primeira resposta) — que apareceria como um piscar de números.
  // ⚠️⚠️ ABRE NO MÊS CORRENTE (17/09/2026), e isto MUDOU: o painel abria em
  //    "todos os meses". Com a rede inteira dentro, "tudo" vira um número que só
  //    cresce e não descreve nenhuma decisão — a gestão pergunta "como estamos
  //    ESTE mês". O seletor continua ali para abrir a série inteira.
  // ⚠️ `toISOString` não serve: ele devolve UTC, e no dia 1º antes das 21h (fuso
  //    -3) ele ainda diz o mês anterior — o painel abriria no mês passado.
  const mesCorrente = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [mes, setMes] = useState(mesCorrente);
  const [loja, setLoja] = useState('');
  // ⚠️⚠️ O SLA É POR FILA (17/09/2026). O servidor assume 'TI' quando ninguém diz,
  //    então sem este parâmetro o painel da Manutenção mostraria o desempenho do
  //    TI — um número plausível, alheio e impossível de desconfiar olhando.
  //    ⚠️ Os PRAZOS (`/sla/prazos`) são os mesmos nas duas filas: a régua é a
  //    prioridade, não a equipe. Por isso só esta busca leva a equipe.
  //    (o estado da fila saiu: quem a escolhe é a faixa do hub)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      // ⚠️ `encodeURIComponent` na loja: `ESCRITÓRIO` tem acento, e sigla nova
      //    pode ter espaço (a `dtbChamado` guarda `MAL CK 3` em outras colunas).
      const q = new URLSearchParams();
      if (mes) q.set('mes', mes);
      if (loja) q.set('loja', loja);
      q.set('equipe', equipe);
      const r = await fetch(`${API}/indicadores?${q.toString()}`, { headers: auth() });
      if (!r.ok) {
        // ⚠️ 404 é o sintoma da JANELA DE DEPLOY (o `build/` sobe antes do
        //    `pm2 reload`, então a tela nova conversa com o backend velho por
        //    alguns segundos). Dizer "recarregue" resolve; "erro ao carregar"
        //    manda a pessoa procurar um problema que não existe.
        throw new Error(r.status === 404
          ? 'O painel acabou de ser publicado. Recarregue a página (Ctrl+Shift+R).'
          : 'Não foi possível carregar os indicadores.');
      }
      setDados(await r.json());
    } catch (e) {
      setErro(e.message);
      setDados(null);
    } finally {
      setCarregando(false);
    }
    // ⚠️ `equipe` na lista: trocar de fila tem de refazer a conta. Sem ela, os
    //    botões mudariam e os números seguiriam sendo os da fila anterior.
  }, [mes, loja, equipe]);

  useEffect(() => { carregar(); }, [carregar]);

  // A tabela de prazos vem da API.
  //
  // ⚠️⚠️ ELA ESTAVA ESCRITA À MÃO AQUI, e isso a tornava a SEGUNDA cópia do
  //    `PRAZOS` do `chamados-sla.js`. Com a tabela também na tela de abertura
  //    seriam TRÊS, e mudar um prazo exigiria acertar as três sem nada que
  //    reprovasse quem esquecesse — o sintoma seria a tela prometendo um prazo
  //    que o indicador ao lado não usa para cobrar.
  // ⚠️ Falha silenciosa: sem os prazos o bloco explicativo não abre, e o
  //    indicador continua funcionando. É apoio à leitura, não o número.
  const [prazos, setPrazos] = useState(null);
  useEffect(() => {
    fetch(`${API}/sla/prazos`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setPrazos(d && Array.isArray(d.faixas) && d.faixas.length ? d : null))
      .catch(() => setPrazos(null));
  }, []);

  const ind = dados?.indicador;
  const lista = dados?.chamados || [];

  // Os chamados FORA do prazo de atendimento, os mais estourados primeiro.
  // ⚠️⚠️ A LISTA EXISTE PORQUE NÚMERO SEM CAMINHO PARA O CASO CONCRETO NÃO É
  //    USADO. "3 fora do SLA" sem dizer QUAIS obriga a pessoa a caçar na fila, e
  //    na prática ela não caça — o painel vira decoração.
  const fora = lista
    .filter((c) => c.sla?.atendimento?.situacao === 'fora')
    .sort((a, b) => (b.sla.atendimento.consumido - b.sla.atendimento.permitido)
      - (a.sla.atendimento.consumido - a.sla.atendimento.permitido));

  const semPrazo = lista.filter((c) => c.sla?.motivoDeFora === 'semPrioridade');

  const card = (cor) => ({
    flex: '1 1 200px', minWidth: 190, padding: 16, borderRadius: 12,
    border: `1px solid ${cor}22`, backgroundColor: `${cor}0d`,
  });
  const rotulo = { margin: 0, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 };
  // ⚠️ `tabular-nums`: sem isso os dígitos dançam de largura e a coluna de
  //    números fica desalinhada a cada atualização.
  const numero = (cor) => ({ margin: '6px 0 0 0', fontSize: 28, fontWeight: 800, color: cor, fontVariantNumeric: 'tabular-nums' });
  const nota = { margin: '4px 0 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 };
  const inputData = {
    padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
    fontSize: 13, fontFamily: 'inherit',
  };

  const th = {
    textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0',
  };
  const td = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9' };

  // =====================================================================
  // AS DUAS LISTAS EM CARTÃO (celular).
  //
  // ⚠️⚠️ NÃO SÃO AS TABELAS ENCOLHIDAS — a hierarquia muda junto com o formato,
  //    pelo mesmo motivo do `cartaoDoChamado` da Consulta. Na tabela toda coluna
  //    pesa igual, porque o cabeçalho diz o que cada uma é. No cartão não há
  //    cabeçalho: alguma coisa tem de mandar, e o que manda é **o número que
  //    colocou aquela linha ali**.
  //
  // ⚠️ Estas listas são de LEITURA, não de trabalho: o cartão é `<div>` e não
  //    `<button>`. O da Consulta é clicável porque abre o chamado; aqui não há
  //    para onde ir, e um cartão que parece botão e não faz nada é pior que um
  //    que não parece.
  // =====================================================================

  /** Estilo comum aos dois cartões — a faixa colorida à esquerda é o que os
   *  separa visualmente num rolar rápido. */
  const cartaoBase = (corDaFaixa) => ({
    border: '1px solid #e2e8f0', borderLeft: `4px solid ${corDaFaixa}`,
    borderRadius: 10, backgroundColor: '#fff', padding: 12, marginBottom: 10,
  });
  /** A primeira linha: identificador à esquerda, o número que manda à direita. */
  const cartaoTopo = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' };
  const cartaoId = { fontWeight: 800, fontSize: 13, color: COR, fontVariantNumeric: 'tabular-nums' };
  const cartaoTitulo = { margin: '0 0 6px 0', fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.35 };
  const cartaoMeta = { display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 12, color: '#64748b' };

  /**
   * Um chamado FORA do prazo, em cartão.
   *
   * ⚠️ QUEM MANDA É O ESTOURO (`+2h30`), e não o `#id`: é ele que ordena a lista
   *    e é a razão de a linha existir. Na tabela ele era a **última** coluna, a
   *    620px da esquerda — a primeira coisa a sair da tela num aparelho.
   * ⚠️ Consumido e prazo descem para a linha de baixo: são a JUSTIFICATIVA do
   *    estouro, e quem precisa dela já parou para ler o cartão.
   */
  const cartaoForaDoPrazo = (c) => {
    const a = c.sla.atendimento;
    return (
      <div key={c.ID} style={cartaoBase('#dc2626')}>
        <div style={cartaoTopo}>
          <span style={cartaoId}>#{c.ID}</span>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {c.PRIORIDADE === 'Media' ? 'Média' : c.PRIORIDADE}
          </span>
          <span style={{
            marginLeft: 'auto', fontSize: 13, fontWeight: 800,
            fontVariantNumeric: 'tabular-nums', color: '#dc2626',
          }}>
            +{fmtDuracao(a.consumido - a.permitido)}
          </span>
        </div>
        <p style={cartaoTitulo}>{c.TITULO}</p>
        <div style={cartaoMeta}>
          <span style={{ fontWeight: 700, color: '#334155' }}>{c.LOJA}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmtDuracao(a.consumido)} de {fmtDuracao(a.permitido)}
          </span>
          {/* Mesma razão da tabela: sem isto, "o técnico demorou" e "o chamado
              esperou um terceiro" se leem igual. */}
          {a.pausado > 0 && <span>já sem {fmtDuracao(a.pausado)} de pausa</span>}
        </div>
      </div>
    );
  };

  /**
   * Um chamado SEM PRIORIDADE (e por isso sem prazo), em cartão.
   *
   * ⚠️ Aqui não há estouro para mostrar — o que manda é **desde quando ele está
   *    à deriva**, porque o problema destes chamados é justamente ninguém os ter
   *    triado. A situação vira texto ao lado da loja: ela é contexto, não o
   *    número da leitura.
   */
  const cartaoSemPrazo = (c) => (
    <div key={c.ID} style={cartaoBase('#f59e0b')}>
      <div style={cartaoTopo}>
        <span style={cartaoId}>#{c.ID}</span>
        <span style={{
          marginLeft: 'auto', fontSize: 12, fontWeight: 700,
          fontVariantNumeric: 'tabular-nums', color: '#92400e',
        }}>
          aberto {fmtData(c.ABERTO_EM)}
        </span>
      </div>
      <p style={cartaoTitulo}>{c.TITULO}</p>
      <div style={cartaoMeta}>
        <span style={{ fontWeight: 700, color: '#334155' }}>{c.LOJA}</span>
        <span>{c.STATUS}</span>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
        justifyContent: 'space-between', marginBottom: 18,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
            Indicadores de SLA
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>
            {/* ⚠️ O cabeçalho REPETE o filtro em palavras. Sem isso, um % de 100%
                numa loja só se lê como o número da rede — e o `<select>` ao lado
                é fácil de não notar depois de alguns minutos na tela. */}
            Chamados abertos {mes ? <strong>em {rotuloDoMes(mes)}</strong> : <>em <strong>todos os meses</strong></>}
            {loja ? <> na <strong>{loja}</strong></> : <> em <strong>todas as lojas</strong></>}.
            {' '}O prazo corre da <strong>abertura</strong> — é o que a loja sente.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* ⚠️ O SELETOR DE FILA SAIU DAQUI em 17/09/2026, no mesmo dia em que
              entrou: quem troca a fila é a FAIXA do hub, acima das abas. Dois
              controles para a mesma pergunta viram duas verdades. */}
          {/* ⚠️⚠️ AS OPÇÕES VÊM DA RESPOSTA, nunca de uma lista montada aqui: só o
              servidor sabe quais meses e lojas têm chamado — e ele já aplicou o
              `recorteLoja`, então o gerente vê só a própria loja. Uma lista fixa
              das 13 lojas ofereceria opção que devolve painel vazio.
              ⚠️ Enquanto a 1ª resposta não chega, os `<select>` têm só a opção
              "todos" — e é o certo: oferecer opção antes de saber se ela existe é
              o que produz o filtro que não acha nada. */}
          <select
            style={inputData}
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            aria-label="Período"
          >
            <option value="">Todos os meses</option>
            {(dados?.filtros?.meses || []).map((m) => (
              <option key={m} value={m}>{rotuloDoMes(m)}</option>
            ))}
          </select>
          <select
            style={inputData}
            value={loja}
            onChange={(e) => setLoja(e.target.value)}
            aria-label="Loja"
          >
            <option value="">Todas as lojas</option>
            {(dados?.filtros?.lojas || []).map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {erro && (
        <div style={{
          padding: 14, borderRadius: 8, backgroundColor: '#fef2f2',
          border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, marginBottom: 16,
        }}>
          {erro}
        </div>
      )}

      {carregando && <p style={{ fontSize: 13, color: '#64748b' }}>Apurando…</p>}

      {/* ⚠️ O PAINEL DE GESTÃO VEM ANTES DO SLA (17/09/2026), e a ordem é a
          decisão: "onde a fila está agora" é o que pede ação hoje; o % no prazo é
          o retrato do que já passou. Quem abre a tela para trabalhar precisa
          primeiro do que está em atraso, não do percentual do mês. */}
      {!carregando && dados?.painel && (
        <ChamadosPainelGestao painel={dados.painel} ehMobile={ehMobile} />
      )}

      {/* ⚠️ Mês corrente vazio NÃO é tela em branco: a pessoa precisa saber que o
          período é que está vazio — e ter o caminho para a série inteira, senão
          ela conclui que o painel quebrou. */}
      {!carregando && dados?.painel?.cartoes?.total === 0 && mes && (
        <p style={{
            margin: '0 0 16px 0', padding: '12px 14px', borderRadius: 10,
            backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
            fontSize: 13, color: '#475569',
        }}>
          Nenhum chamado aberto em <strong>{rotuloDoMes(mes)}</strong>.{' '}
          <button
            type="button"
            onClick={() => setMes('')}
            style={{
                border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                color: 'var(--ch-cor)', fontWeight: 700, fontSize: 13, textDecoration: 'underline',
            }}
          >
            Ver todos os meses
          </button>
        </p>
      )}

      {!carregando && ind && (
        <>
          {/* ============ O INDICADOR, e os números que o tornam legível ============ */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            {/* ⚠️⚠️ `pct === null` (denominador zero) mostra TRAVESSÃO, nunca 0%.
                Zero se lê como "nenhum chamado no prazo" — o oposto de "não há o
                que medir ainda". No início da série e em filtro estreito isto
                acontece de verdade. Mesma regra do total de peças do módulo. */}
            <div style={card(ind.atendimento.pct == null ? '#64748b'
              : (ind.atendimento.naMeta ? '#059669' : '#dc2626'))}>
              <p style={rotulo}>Atendidos no prazo</p>
              <p style={numero(ind.atendimento.pct == null ? '#64748b'
                : (ind.atendimento.naMeta ? '#059669' : '#dc2626'))}>
                {ind.atendimento.pct == null ? '—' : `${ind.atendimento.pct}%`}
              </p>
              <p style={nota}>
                {ind.atendimento.pct == null
                  ? 'Nenhum chamado com prazo vencido ou atendido neste período.'
                  : <>
                    {ind.atendimento.dentro} de {ind.atendimento.base} · meta {ind.meta}%
                    {ind.atendimento.naMeta
                      ? <> — <strong style={{ color: '#059669' }}>na meta</strong></>
                      : <> — <strong style={{ color: '#dc2626' }}>abaixo da meta</strong></>}
                  </>}
              </p>
            </div>

            {/* ⚠️ A solução vem SEM META, de propósito: a meta de 90% que a Julia
                definiu é a do ATENDIMENTO. Pôr a mesma meta aqui inventaria um
                compromisso que ninguém assumiu. */}
            <div style={card('#0e7490')}>
              <p style={rotulo}>Solucionados no prazo</p>
              <p style={numero('#0e7490')}>
                {ind.solucao.pct == null ? '—' : `${ind.solucao.pct}%`}
              </p>
              <p style={nota}>
                {ind.solucao.pct == null
                  ? 'Sem chamado encerrado ou vencido aqui.'
                  : `${ind.solucao.dentro} de ${ind.solucao.base} · acompanhamento, sem meta`}
              </p>
            </div>

            <div style={card('#b45309')}>
              <p style={rotulo}>Sem prioridade</p>
              <p style={numero('#b45309')}>{ind.semPrioridade}</p>
              <p style={nota}>
                Não triados: <strong>não existe prazo a aplicar</strong>, então eles
                ficam fora do percentual. É a fila do Gerente de TI.
              </p>
            </div>

            <div style={card('#64748b')}>
              <p style={rotulo}>Com relógio pausado</p>
              <p style={numero('#64748b')}>{ind.comPausa}</p>
              <p style={nota}>
                Esperaram a loja ou um terceiro. O prazo fica parado nesse tempo —
                e é por isso que este número existe.
              </p>
            </div>
          </div>

          {/* ============ A DISTRIBUIÇÃO DE PRIORIDADE ============ */}
          {/* ⚠️⚠️ ESTE BLOCO EXISTE POR UM ACHADO: em 09/09 os 12 chamados triados
              eram TODOS "Média" (0 Alta, 0 Baixa) — o mesmo vício medido no Auvo,
              onde 99,6% era "Média". Com uma faixa só em uso, a tabela de três
              prazos é decorativa e o % descreve uma régua única. O painel não
              conserta isso (é conduta, não código), mas deixa de esconder. */}
          <div style={{
            padding: 16, borderRadius: 12, border: `1px solid ${COR_CLARA}`,
            marginBottom: 20,
          }}>
            <p style={{ ...rotulo, marginBottom: 10 }}>Distribuição de prioridade</p>
            {Object.keys(ind.porPrioridade).length === 0 ? (
              <p style={nota}>Nenhum chamado triado no período.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                {['Alta', 'Media', 'Baixa'].map((p) => (
                  <div key={p}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      {p === 'Media' ? 'Média' : p}
                    </span>
                    <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                      {ind.porPrioridade[p] || 0}
                    </p>
                  </div>
                ))}
                {/* ⚠️ Só avisa quando há concentração REAL (uma faixa só, com
                    volume): um alerta permanente vira ruído que ninguém lê. */}
                {Object.keys(ind.porPrioridade).length === 1 && ind.atendimento.base >= 5 && (
                  <p style={{ ...nota, flex: '1 1 260px', color: '#b45309' }}>
                    <AlertTriangle size={13} style={{ verticalAlign: -2 }} />{' '}
                    Todos numa faixa só — na prática a régua de três prazos está
                    valendo como uma. Vale conferir se a triagem está classificando.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ============ AS DUAS PERNAS, EM BARRAS ============ */}
          <div style={{
            padding: 16, borderRadius: 12, border: `1px solid ${COR_CLARA}`, marginBottom: 20,
          }}>
            <p style={{ ...rotulo, marginBottom: 4 }}>Atendimento × solução</p>
            <p style={{ ...nota, marginBottom: 12 }}>
              "Ainda no prazo" é o que não venceu — não entra no percentual.
            </p>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart
                  data={[
                    { etapa: 'Atendimento', perna: ind.atendimento },
                    { etapa: 'Solução', perna: ind.solucao },
                  ].map(({ etapa, perna }) => ({
                    etapa,
                    // Chaves = os rótulos do mapa, para o `dataKey` de cada Bar
                    // sair do mesmo lugar que a legenda e a cor.
                    ...SITUACOES.reduce((acc, s) => {
                      const contagem = { dentro: perna.dentro, fora: perna.fora, aguardando: perna.aguardando };
                      return { ...acc, [ROTULO_SITUACAO[s]]: contagem[s] };
                    }, {}),
                  }))}
                  margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="etapa" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  {SITUACOES.map((s, i) => (
                    <Bar
                      key={s}
                      dataKey={ROTULO_SITUACAO[s]}
                      stackId="a"
                      fill={COR_SITUACAO[s]}
                      /* Só o topo da pilha arredonda. */
                      radius={i === SITUACOES.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ============ QUEM ESTOUROU O PRAZO ============ */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ ...rotulo, marginBottom: 8 }}>
              Fora do prazo de atendimento ({fora.length})
            </p>
            {fora.length === 0 ? (
              <p style={nota}>
                <CheckCircle2 size={14} color="#059669" style={{ verticalAlign: -2 }} />{' '}
                Nenhum chamado estourou o prazo de atendimento no período.
              </p>
            ) : ehMobile ? (
              // ⚠️⚠️ NO CELULAR ESTA TABELA VIRA CARTÃO, e não é a tabela
              //    encolhida: são 7 colunas e 620px de largura mínima, contra
              //    ~360px de aparelho. O ESTOURO — o número que ordena a lista e
              //    a razão de a linha existir — ficava fora da tela, na última
              //    coluna, justamente na leitura com pressa.
              <div>{fora.slice(0, 20).map(cartaoForaDoPrazo)}</div>
            ) : (
              // ⚠️ `overflow-x: auto` no contêiner: tabela larga tem de rolar
              //    dentro dela, nunca fazer a página inteira rolar de lado.
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                  <thead>
                    <tr>
                      <th style={th}>#</th>
                      <th style={th}>Loja</th>
                      <th style={th}>Assunto</th>
                      <th style={th}>Prioridade</th>
                      <th style={th}>Consumido</th>
                      <th style={th}>Prazo</th>
                      <th style={th}>Estouro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fora.slice(0, 20).map((c) => {
                      const a = c.sla.atendimento;
                      return (
                        <tr key={c.ID}>
                          <td style={{ ...td, fontWeight: 700, color: COR }}>{c.ID}</td>
                          <td style={td}>{c.LOJA}</td>
                          <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.TITULO}
                          </td>
                          <td style={td}>{c.PRIORIDADE === 'Media' ? 'Média' : c.PRIORIDADE}</td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtDuracao(a.consumido)}
                            {/* ⚠️ Dizer que houve pausa é o que separa "o técnico
                                demorou" de "o chamado esperou um terceiro". Sem
                                isto, os dois casos se leem igual. */}
                            {a.pausado > 0 && (
                              <span style={{ color: '#64748b', fontSize: 11 }}>
                                {' '}(já sem {fmtDuracao(a.pausado)} de pausa)
                              </span>
                            )}
                          </td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                            {fmtDuracao(a.permitido)}
                          </td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: '#dc2626', fontWeight: 700 }}>
                            +{fmtDuracao(a.consumido - a.permitido)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* ⚠️ FORA dos dois ramos: o corte em 20 vale para a tabela E para o
                cartão. Deixá-lo só dentro da tabela faria o celular mostrar 20
                de 47 sem dizer que há 47 — o pior tipo de omissão num painel. */}
            {fora.length > 20 && (
              <p style={nota}>Mostrando os 20 maiores estouros de {fora.length}.</p>
            )}
          </div>

          {/* ============ OS SEM PRAZO ============ */}
          {/* ⚠️⚠️ ELES TÊM LISTA PRÓPRIA, e não somem nem entram no %: sem
              prioridade não há prazo — mas há um número, e é o número que o
              Gerente de TI precisa ver. Foram 56% da base em 09/09. */}
          {semPrazo.length > 0 && (
            <div style={{
              padding: 16, borderRadius: 12, backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
            }}>
              <p style={{ ...rotulo, color: '#92400e', marginBottom: 6 }}>
                <HelpCircle size={13} style={{ verticalAlign: -2 }} />{' '}
                Sem prioridade — sem prazo aplicável ({semPrazo.length})
              </p>
              <p style={{ ...nota, color: '#92400e', marginBottom: 10 }}>
                Estes chamados <strong>não entram no percentual</strong> porque
                ninguém definiu a prioridade deles. A triagem é o que lhes dá prazo.
              </p>
              {ehMobile ? (
                <div>{semPrazo.slice(0, 20).map(cartaoSemPrazo)}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                    <thead>
                      <tr>
                        <th style={th}>#</th>
                        <th style={th}>Loja</th>
                        <th style={th}>Assunto</th>
                        <th style={th}>Aberto em</th>
                        <th style={th}>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {semPrazo.slice(0, 20).map((c) => (
                        <tr key={c.ID}>
                          <td style={{ ...td, fontWeight: 700, color: COR }}>{c.ID}</td>
                          <td style={td}>{c.LOJA}</td>
                          <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.TITULO}
                          </td>
                          <td style={td}>{fmtData(c.ABERTO_EM)}</td>
                          <td style={td}>{c.STATUS}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {semPrazo.length > 20 && (
                <p style={nota}>Mostrando 20 de {semPrazo.length}.</p>
              )}
            </div>
          )}

          {/* ============ A RÉGUA, ESCRITA ============ */}
          {/* ⚠️ A tabela de prazos fica NA TELA, e não só na documentação: quem lê
              "abaixo da meta" precisa poder conferir contra o que, sem sair daqui
              e sem perguntar a ninguém. */}
          <details style={{ marginTop: 20, fontSize: 13, color: '#475569' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700, color: COR }}>
              Como o prazo é contado
            </summary>
            <div style={{ padding: '10px 0 0 0', lineHeight: 1.6 }}>
              {prazos && (
                <table style={{ borderCollapse: 'collapse', marginBottom: 10 }}>
                  <thead>
                    <tr>
                      <th style={th}>Prioridade</th>
                      <th style={th}>Atendimento</th>
                      <th style={th}>Solução</th>
                      <th style={th}>Contagem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prazos.faixas.map((f) => (
                      <tr key={f.prioridade}>
                        <td style={td}>{SINAL_PRIORIDADE[f.prioridade] || ''} {f.rotulo}</td>
                        <td style={td}>{f.atendimento} {f.unidade}</td>
                        <td style={td}>{f.solucao} {f.unidade}</td>
                        <td style={td}>{f.corrida ? 'corridas, 24/7' : 'seg–sex'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p style={{ margin: '0 0 6px 0' }}>
                <Clock size={13} style={{ verticalAlign: -2 }} /> Os dois prazos
                correm da <strong>abertura</strong>: atendimento até o técnico
                assumir, solução até o serviço estar feito.
              </p>
              <p style={{ margin: '0 0 6px 0' }}>
                <PauseCircle size={13} style={{ verticalAlign: -2 }} /> O relógio
                <strong> pausa</strong> enquanto o chamado espera a loja ou um
                terceiro (peça, Invgate, reparo) — e nesse caso o técnico registra
                o motivo e a <strong>previsão de conclusão</strong>.
              </p>
              <p style={{ margin: 0 }}>
                Dias úteis são seg–sex, descontando feriado federal e estadual.
                {/* ⚠️ Diz por que o municipal fica fora: é a pergunta que alguém
                    de Vitória vai fazer ao ver o prazo correr no 08/09. */}
                {' '}O <strong>municipal não entra</strong>: a rede está em
                municípios diferentes, e descontá-lo daria prazos diferentes por
                loja para a mesma prioridade.
              </p>
            </div>
          </details>
        </>
      )}
    </div>
  );
};

export default ChamadosIndicadores;
